"use client";

import Field2 from "components/form/Field2";
import {
  Button,
  Container,
  OutsideClickHandler,
  Typography,
} from "components/ct-design";
import SwapIcon from "icons/swapSRP.svg";
import DownChevron from "icons/chevronSmall.svg";

import "./style.scss";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";
import useBusSearch from "src/customHooks/useBusSearch";
import DropdownOptions from "components/dropdown/dropDownOptions";
import { ResultsContext } from "src/context/resultsContext/context";
import { getSearchParams, handleDisabledDates } from "src/utils/generalUtils";
import Calendar from "components/calendar";
import { sendBusUIAction } from "src/analytics/useAnalytics";
import useGetChartData from "./customHooks/useGetChartData";
const EditSearch = () => {
  const { resultsData, showSeatLayout } = useContext(ResultsContext);

  const { handleHideSeatLayout } = useGetChartData();
  const { fromCityName, toCityName, fromCity, toCity, journeyDate } =
    getSearchParams();
  const {
    searchValue,
    handleSearchValue,
    activeList,
    setActiveList,
    CityListComp,
    handleOnChange,
    openDropDownList,
    setSearchValue,
    setOpenDropDownList,
    error,
    setCalendarValue,
    handleSearch,
    calendarValue,
    setSelectedCity,
    setCitiesList,
    handleSwap,
    handlePopularDestinations,
    handleOverlay,
    setNoData,
  } = useBusSearch("editSearch");

  const chevronClass = classNames({
    "rotate-clockwise": calendarValue.open,
    "rotate-antiClockwise": !calendarValue.open,
  });

  useEffect(() => {
    setSearchValue({
      arrival: resultsData?.sc?.toCityName || toCityName,
      departure: resultsData?.sc?.fromCityName || fromCityName,
    });
    setCalendarValue({
      ...calendarValue,
      selectedDate: dayjs(
        resultsData?.sc ? resultsData?.sc?.journeyDate : journeyDate
      ).toDate(),
    });
    setSelectedCity({
      arrival: {
        cityName: resultsData?.sc?.toCityName || toCityName,
        cityId: resultsData?.sc?.toCityId || parseInt(toCity),
      },
      departure: {
        cityName: resultsData?.sc?.fromCityName || fromCityName,
        cityId: resultsData?.sc?.fromCityId || parseInt(fromCity),
      },
    });
    if (resultsData?.sc)
      handlePopularDestinations(
        resultsData?.sc?.fromCityId,
        resultsData?.sc?.fromCityName
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsData?.sc?.journeyDate, journeyDate]);

  const editSearchRef = useRef(null);

  const [isSticky, setIsSticky] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
      handleOverlay(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnFocus = (type: "departure" | "arrival") => {
    setActiveList(type);
    setOpenDropDownList({ arrival: false, departure: true });
    if (type === "departure") {
      setOpenDropDownList({ arrival: false, departure: true });

      setCitiesList((prev) => ({
        ...prev,
        ["departure"]: {
          ...prev["departure"],
          suggestions: [],
        },
      }));
    } else setOpenDropDownList({ arrival: true, departure: false });
    if (calendarValue.open) setCalendarValue({ ...calendarValue, open: false });
    handleOverlay(true);
  };

  const handleOutsideClick = useCallback(() => {
    setOpenDropDownList({
      ...openDropDownList,
      [activeList]: false,
    });

    if (calendarValue.open) setCalendarValue({ ...calendarValue, open: false });

    handleOverlay(false);
  }, [
    activeList,
    calendarValue,
    handleOverlay,
    openDropDownList,
    setCalendarValue,
    setOpenDropDownList,
  ]);

  const handleCalendarBtn = () => {
    setCalendarValue({
      ...calendarValue,
      open: !calendarValue.open,
    });
    setOpenDropDownList({ arrival: false, departure: false });
    if (!calendarValue.open) handleOverlay(true);
    else handleOverlay(false);
  };

  const handleSelectDate = (value: Date) => {
    if (value) {
      setCalendarValue({
        open: false,
        selectedDate: dayjs(value).toDate(),
      });
      handleOverlay(false);
    } else {
      setCalendarValue({ ...calendarValue, open: false });
      handleOverlay(false);
    }
  };

  const handleSearchBtn = () => {
    const dx = Math.ceil(
      dayjs(calendarValue?.selectedDate).diff(dayjs(), "day", true)
    );
    const differenceFromOldDate = Math.ceil(
      dayjs(calendarValue?.selectedDate).diff(dayjs(journeyDate), "day", true)
    );
    sendBusUIAction(
      {
        actionName: "srpDateChanged",
        sourceCity: fromCityName,
        destinationCity: toCityName,
        travelDate: dayjs(calendarValue?.selectedDate).format("YYYY-MM-DD"),
        oldDate: journeyDate,
        dx,
        differenceFromOldDate,
      },
      "b_srp_pageload"
    );

    if (showSeatLayout) handleHideSeatLayout();
    handleSearch();
  };

  return (
    <Container
      className={`${
        isSticky ? "p-sticky t-0  w-100 bg-neutral-100" : "p-relative"
      }  w-100p z-50 bg-neutral-100`}
      boxShadow={isSticky ? "0px 4px 16px 0px rgba(26, 26, 26, 0.08)" : ""}
    >
      <OutsideClickHandler onOutsideClick={handleOutsideClick}>
        <div
          ref={editSearchRef}
          style={{ maxWidth: "1224px", margin: "auto", padding: " 0 8px" }}
        >
          <Container className="flex w-100p flex-center py-4 z-50 ">
            <div className="p-relative">
              <Field2
                key="departureField"
                wrapperclass="flex-1 edit-field flex-aroun"
                value={searchValue.departure}
                onChange={(e) => {
                  handleSearchValue(e.target.value);
                  if (e.target.value.length >= 3)
                    handleOnChange(e.target.value, "departure");
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleOnFocus("departure");
                  setNoData((prev) => ({ ...prev, departure: false }));
                }}
                hasError={error.departure}
                placeholder="Departure"
              />
              <DropdownOptions
                openDropdown={openDropDownList.departure}
                wrapperClass="p-absolute bg-neutral-100 z-10 edit-search-dropdown-options"
              >
                {CityListComp}
              </DropdownOptions>
            </div>
            <SwapIcon onClick={handleSwap} className="mx-4" />

            <div className="p-relative">
              <Field2
                key="arrivalField"
                wrapperclass="flex-1 edit-field"
                value={searchValue.arrival}
                onChange={(e) => {
                  handleSearchValue(e.target.value);
                  if (e.target.value.length >= 3)
                    handleOnChange(e.target.value, "arrival");
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleOnFocus("arrival");
                  setNoData((prev) => ({ ...prev, arrival: false }));
                }}
                placeholder="Arrival"
                hasError={error.arrival}
              />
              <DropdownOptions
                openDropdown={openDropDownList.arrival}
                wrapperClass="p-absolute bg-neutral-100 z-10 edit-search-dropdown-options"
              >
                {CityListComp}
              </DropdownOptions>
            </div>

            <div className="p-relative" style={{ width: "16%" }}>
              <div
                className="flex-1 edit-calendar-field ba-solid bc-line-500 br-6 flex-between px-3 c-pointer flex flex-middle ml-4 h-100p w-100p "
                onClick={handleCalendarBtn}
              >
                <Typography variant="B2">
                  {dayjs(calendarValue.selectedDate).format("ddd, DD MMM")}
                </Typography>
                <DownChevron className={chevronClass} />
              </div>
              {calendarValue.open ? (
                <Container
                  className="z-90 p-absolute mt-2 bg-neutral-100"
                  css={{ top: "30px", width: "200%" }}
                >
                  <Container
                    className="p-absolute mt-1 bg-neutral-100 br-16 px-5 pt-5 "
                    boxShadow={"0px 4px 16px 0px rgba(26, 26, 26, 0.08)"}
                  >
                    <Calendar
                      disabledDays={handleDisabledDates}
                      selectedDays={calendarValue.selectedDate}
                      mode="single"
                      noOfMonths={1}
                      fromMonth={dayjs().startOf("month").toDate()}
                      toDate={dayjs().add(90, "day").toDate()}
                      onDateSelect={handleSelectDate}
                      defaultMonth={dayjs(calendarValue.selectedDate).toDate()}
                    />
                  </Container>
                </Container>
              ) : null}
            </div>

            <Button
              style={{ maxWidth: "160px", flex: "1", marginLeft: "16px" }}
              onClick={handleSearchBtn}
            >
              Search
            </Button>
          </Container>
        </div>
      </OutsideClickHandler>
    </Container>
  );
};

export default EditSearch;
